/**
 * Unified ShareModal component for workspaces, views, and interactions.
 *
 * Self-contained: internally fetches and mutates sharing state via
 * the universal sharing hooks. Callers only need to provide the entity
 * type, ID, and name.
 *
 * Feature matrix (derived from entityType):
 *   workspace:   org toggle, link sharing toggle, copy link, email, permissions
 *   view:        org toggle, copy link, email, permissions
 *   interaction: org toggle, privacy indicator, copy link, email, permissions
 *   conversation / agent / skill / artifact: as documented at each gate below
 *
 * Every type offers Email — mailing a link to the people who already have
 * access (#10815, generalised from interactions on Hunter's ruling).
 */

import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  MagnifyingGlass,
  LockKey,
  Buildings,
  UsersThree,
  Link as LinkIcon,
  X,
  Check,
  EnvelopeSimple,
} from "@phosphor-icons/react";
import { Tooltip } from "@mui/material";
import { useSelector } from "react-redux";

import type {
  EntityType,
  NotifySkipped,
  SharePayload,
  SharedUser,
  SharedTeam,
  NotOrganizationWideWidget,
} from "@/services/sharing";
import {
  SHARING_QUERY_KEYS,
  useSharingStatus,
  useUpdateSharing,
  useNotifyShare,
} from "@/hooks/useSharing";
import { useQueryClient } from "@tanstack/react-query";
import EmailNotePanel from "./EmailNotePanel";
import { emailNounFor } from "./emailNoun";
import InkSelect from "./InkSelect";
import AccessList from "./controls/AccessList";
import GeneralAccessControl from "./controls/GeneralAccessControl";
import GranteePicker from "./controls/GranteePicker";
import { type AccessItem, type GeneralAccess } from "./controls/shareParts";
import { useGranteeSearch } from "./controls/useGranteeSearch";
import { createInteractionSlug } from "@/components/FpPages/RMS/Dashboard/utils/slugUtils";
import { toConversationSlug } from "@/components/FpPages/Chat/chatUtils";
import { clearProductTimeout, setProductTimeout } from "@/utils/productTimers";
import { displayOrganizationName } from "@/utils/organizationName";
import { componentLibraryPath } from "@/components/FpPages/RMS/Components/appearanceLocation";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  entityType: EntityType;
  entityId: string;
  entityName: string;
  /** Workspace/view: callback to copy a shareable link */
  onCopyLink?: () => void;
  /** Whether the current user owns this entity (controls edit ability) */
  isOwner?: boolean;
  /** How many library components a dashboard's widgets use: sharing the dashboard shares them too (#12952). */
  componentCount?: number;
  /**
   * The role the organization gets when General access is opened to it. Editor by default; a
   * dashboard's opens as Viewer (dashboard-assistant-polish-4 requirement 19), and a component's
   * is always Viewer.
   */
  organizationJoinsAs?: "view" | "edit";
  /**
   * Take the view out of one of the groups that hold it (#13896). The RMS
   * host owns the write; offered on a view's group rows when the response
   * says the caller may (`can_remove_from_groups`).
   */
  onRemoveFromGroup?: (groupId: number) => Promise<unknown>;
  /**
   * A member who asked for access (round 10 U4): shown as a Viewer, unsaved, until Save sends
   * the grant and the usual share mail. An id that is not among the members who can be added
   * (not in the organization, or already holding access) adds nobody.
   */
  pendingGrantee?: string | null;
}

/**
 * The organization as General access names it: "Acme Organization" for Acme, and a name that
 * already ends in the word left as it is, never "Acme Organization Organization" (UX walk item
 * 26, dashboard-assistant-polish-4 requirement 19).
 */
const organizationLabel = (name: string): string =>
  /\borganization$/i.test(name.trim()) ? name.trim() : `${name} Organization`;

const plural = (count: number, one: string, many: string): string =>
  count === 1 ? one : many;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const EMPTY_USERS: SharedUser[] = [];
const EMPTY_TEAMS: SharedTeam[] = [];

const ShareModal: React.FC<ShareModalProps> = ({
  open,
  onClose,
  entityType,
  entityId,
  entityName,
  onCopyLink,
  isOwner = true,
  componentCount,
  organizationJoinsAs = "edit",
  onRemoveFromGroup,
  pendingGrantee = null,
}) => {
  // ---- Redux state for current user ----
  const { organization, userId, userName, userEmail } = useSelector(
    (state: any) => state.userData
  );
  const organizationName = organization?.name
    ? organizationLabel(displayOrganizationName(organization.name))
    : "Organization";
  const displayName: string =
    typeof userName === "string"
      ? userName
      : userName?.first_name || userName?.email || "You";

  // ---- Sharing data ----
  const {
    data: sharingStatus,
    isLoading,
    isError: sharingStatusFailed,
  } = useSharingStatus(
    entityType,
    open ? entityId : null,
    true // include_available when modal is open
  );
  const shareMutation = useUpdateSharing();
  // Bound to the mutation observer, so it is stable across renders and safe as
  // an effect dependency — unlike `shareMutation` itself, which is a new object
  // every render.
  const { reset: resetShare } = shareMutation;

  // ---- Local state ----
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  // Derive general access state from sharingStatus
  const generalAccess: GeneralAccess = useMemo(() => {
    if (!sharingStatus) return "restricted";
    if (sharingStatus.link_sharing) return "link";
    if (sharingStatus.org_id) return "organization";
    return "restricted";
  }, [sharingStatus]);
  // Access the entity inherits from another one (#11941). It is shown as
  // what it is — the organization, or people, reached through the source —
  // and never folded into the entity's own grants, so a save here does not
  // copy it onto the entity where it would outlive the source's sharing.
  const inherited = sharingStatus?.inherited ?? null;
  const inheritedOrgAccess = !!inherited?.org_permission;

  // The asked-for member's row (round 10 U4): its role until saved, whether the owner took it
  // off or saved it, and whether the share mail after the save failed.
  const [pendingType, setPendingType] = useState<"view" | "edit">("view");
  const [pendingDone, setPendingDone] = useState(false);
  const [pendingMailFailed, setPendingMailFailed] = useState(false);

  // Reset search and copy state when modal closes
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setShowSearchResults(false);
      setLinkCopiedCount(0);
      setEmailMode(false);
      setEmailSentCount(null);
      setEmailSkipped([]);
      setEmailError(null);
      setPendingType("view");
      setPendingDone(false);
      setPendingMailFailed(false);
      // A rejection belongs to the attempt that caused it, not to the next
      // time the modal is opened.
      resetShare();
    }
  }, [open, resetShare]);

  // The mutation rolls its optimistic update back on failure, so without this
  // the control silently returns to its old value and the user has no idea the
  // change was refused (#11172).
  const shareError: string | null = shareMutation.error
    ? shareMutation.error.message
    : null;
  // The widgets the last accepted share of a dashboard could not share (#12952).
  const unsharedWidgets = shareMutation.isSuccess
    ? (shareMutation.data?.widgets?.unshared ?? [])
    : [];
  // Not shared at all (the sharer cannot see it, #13714), versus shared with
  // the people and teams but refused the organization grant (only its owner
  // or an admin can give that).
  const notShareableWidgets = unsharedWidgets.filter(
    (w) => w.reason === "not_shareable"
  );
  const notOrganizationWideWidgets = unsharedWidgets.filter(
    (w): w is NotOrganizationWideWidget => w.reason === "organization"
  );

  // ---- Copy link state ----
  // Bumped on every copy and zeroed on close; the "Copied!" tick shows while
  // it is non-zero and turns itself off 2 s after the latest copy.
  const [linkCopiedCount, setLinkCopiedCount] = useState(0);
  const linkCopied = linkCopiedCount > 0;
  useEffect(() => {
    if (linkCopiedCount === 0) return undefined;
    const timer = setProductTimeout(() => setLinkCopiedCount(0), 2000);
    return () => clearProductTimeout(timer);
  }, [linkCopiedCount]);

  // ---- Email-the-note state (#10815) ----
  const [emailMode, setEmailMode] = useState(false);
  const [emailSentCount, setEmailSentCount] = useState<number | null>(null);
  const [emailSkipped, setEmailSkipped] = useState<NotifySkipped[]>([]);
  const [emailError, setEmailError] = useState<string | null>(null);
  const notifyMutation = useNotifyShare();

  // ---- Feature flags ----
  const showOrgToggle = true; // All entity types support org sharing now
  // An artifact's grants carry Viewer or Editor (#14172), and so do an
  // artifact folder's (#14320), but a new one joins at Viewer: one click must
  // not give a whole organization write access to a table. The creator raises
  // it in the row or the organization dropdown.
  const newGrantType: "view" | "edit" =
    entityType === "artifact" || entityType === "artifact-folder"
      ? "view"
      : "edit";
  // Workspaces (reports, matrices, legacy workspace-backed chats) and agentic
  // chat conversations. Both back it with a grant to the entity's own
  // organization, so the link reaches the org and no further (#11023).
  const showLinkSharing =
    entityType === "workspace" || entityType === "conversation";
  // Emailing is shorthand for "tell the people who can already open this".
  // #10815 built it for meeting notes; it is offered on every share modal
  // since Hunter's ruling (Slack, 2026-09-15). The noun is what the compose
  // step and its confirmation call the item; the modal is not told which
  // kind a workspace is, so it reads the page path the way Copy link does.
  // Read per render, not memoised: the path is not a reactive input.
  const emailNoun = emailNounFor(entityType);
  // Copy link is always available, mirroring Google Drive's share dialog: the
  // copied link works for whoever already has access regardless of the general
  // access setting (e.g. a "Restricted" workspace).
  const showCopyLink = true;

  const searchResults = useGranteeSearch({
    availableUsers: sharingStatus?.available_users ?? EMPTY_USERS,
    availableTeams: sharingStatus?.available_teams ?? EMPTY_TEAMS,
    chosenUsers: sharingStatus?.users ?? EMPTY_USERS,
    chosenTeams: sharingStatus?.teams ?? EMPTY_TEAMS,
    currentUserId: userId,
    query: searchQuery,
    resetKey: `${entityType}:${entityId}`,
  });

  // The member who asked for access, while their grant is unsaved: only someone the dialog
  // could add (a member of the organization not already holding access).
  const pendingUser: SharedUser | null = useMemo(() => {
    if (!pendingGrantee || pendingDone) return null;
    return (
      sharingStatus?.available_users.find((u) => u.id === pendingGrantee) ??
      null
    );
  }, [pendingGrantee, pendingDone, sharingStatus]);

  // ---- Mutation helper ----
  const doShare = useCallback(
    (overrides: Partial<SharePayload>, onSaved?: () => void) => {
      const users =
        overrides.users ??
        sharingStatus?.users.map((u) => ({ id: u.id, type: u.type })) ??
        [];
      const teams =
        overrides.teams ??
        sharingStatus?.teams.map((t) => ({ id: t.id, type: t.type })) ??
        [];
      const orgField =
        overrides.organization !== undefined
          ? overrides.organization
          : sharingStatus?.org_id
            ? {
                type: (sharingStatus.org_permission ?? "edit") as
                  | "view"
                  | "edit",
              }
            : null;
      const linkField =
        overrides.link_sharing !== undefined
          ? overrides.link_sharing
          : (sharingStatus?.link_sharing ?? false);

      const payload: SharePayload = {
        users,
        teams,
        organization: orgField,
        link_sharing: linkField,
        ...(sharingStatus?.updated_at
          ? { updated_at: sharingStatus.updated_at }
          : {}),
      };

      shareMutation.mutate(
        { entityType, entityId, payload },
        onSaved ? { onSuccess: onSaved } : undefined
      );
    },
    [entityType, entityId, sharingStatus, shareMutation]
  );

  // Save the asked-for member's grant, then send them the usual share mail (round 10 U4).
  const handleSavePending = () => {
    if (!pendingUser) return;
    const grantee = pendingUser.id;
    doShare(
      {
        users: [
          ...(sharingStatus?.users.map((u) => ({ id: u.id, type: u.type })) ??
            []),
          { id: grantee, type: pendingType },
        ],
      },
      () => {
        setPendingDone(true);
        notifyMutation.mutate(
          {
            entityType,
            entityId,
            payload: { recipient_user_ids: [grantee] },
          },
          { onError: () => setPendingMailFailed(true) }
        );
      }
    );
  };

  // ---- Event handlers ----
  const handleAddUser = (user: SharedUser) => {
    const newUsers = [
      ...(sharingStatus?.users.map((u) => ({ id: u.id, type: u.type })) ?? []),
      { id: user.id, type: newGrantType },
    ];
    doShare({ users: newUsers });
    setSearchQuery("");
    setShowSearchResults(false);
  };

  const handleAddTeam = (team: SharedTeam) => {
    const newTeams = [
      ...(sharingStatus?.teams.map((t) => ({ id: t.id, type: t.type })) ?? []),
      { id: team.id, type: newGrantType },
    ];
    doShare({ teams: newTeams });
    setSearchQuery("");
    setShowSearchResults(false);
  };

  const handleRemoveAccess = (id: string, kind: "user" | "team") => {
    if (kind === "user" && pendingUser?.id === id) {
      // An unsaved row has nothing to revoke: it is taken off, unsent.
      setPendingDone(true);
      return;
    }
    if (kind === "user") {
      doShare({
        users: (sharingStatus?.users ?? [])
          .filter((u) => u.id !== id)
          .map((u) => ({ id: u.id, type: u.type })),
      });
    } else {
      doShare({
        teams: (sharingStatus?.teams ?? [])
          .filter((t) => t.id !== id)
          .map((t) => ({ id: t.id, type: t.type })),
      });
    }
  };

  const handleChangePermission = (
    id: string,
    kind: "user" | "team",
    perm: "view" | "edit"
  ) => {
    if (kind === "user" && pendingUser?.id === id) {
      setPendingType(perm);
      return;
    }
    if (kind === "user") {
      doShare({
        users: (sharingStatus?.users ?? []).map((u) =>
          u.id === id ? { id: u.id, type: perm } : { id: u.id, type: u.type }
        ),
      });
    } else {
      doShare({
        teams: (sharingStatus?.teams ?? []).map((t) =>
          t.id === id ? { id: t.id, type: perm } : { id: t.id, type: t.type }
        ),
      });
    }
  };

  const handleGeneralAccessChange = (access: GeneralAccess) => {
    if (access === "restricted") {
      doShare({ organization: null, link_sharing: false });
    } else if (access === "organization") {
      doShare({
        organization: {
          type:
            entityType === "component" ||
            entityType === "artifact" ||
            entityType === "artifact-folder"
              ? "view"
              : organizationJoinsAs,
        },
        link_sharing: false,
      });
    } else {
      doShare({ organization: null, link_sharing: true });
    }
  };

  const handleOrgPermissionChange = (perm: "view" | "edit") => {
    doShare({ organization: { type: perm } });
  };

  const handleCopyLink = useCallback(() => {
    if (onCopyLink) {
      // The host mints the link (artifacts); the confirmation is still this
      // modal's to show (#11819).
      onCopyLink();
      setLinkCopiedCount((n) => n + 1);
      return;
    }
    const url = new URL(window.location.href);
    if (entityType === "component") {
      const library = new URL(componentLibraryPath(entityId), url.origin);
      url.pathname = library.pathname;
      url.search = library.search;
      url.hash = "";
    } else if (entityType === "view-group") {
      // A view group has no page of its own: its views are on the RMS strip
      // (#13896), where the notify email links too.
      url.pathname = "/rms";
      url.search = "";
      url.hash = "";
    } else if (entityType === "interaction") {
      // Interactions live at /rms/interaction/{slug}, not the viewer paths
      // the other entity types use.
      const slug = createInteractionSlug({ title: entityName, id: entityId });
      url.pathname = `/rms/interaction/${slug}`;
      url.search = "";
    } else {
      const currentPath = window.location.pathname;
      let basePath = "/chat";
      if (entityType === "agent") {
        // Agents are reachable at /agents/{id}, not the /chat viewer path.
        basePath = "/agents";
      } else if (currentPath.includes("/matrix-templates/")) {
        basePath = "/matrix-templates";
      } else if (currentPath.includes("/matrix/")) {
        basePath = "/matrix";
      } else if (currentPath.includes("/reports/")) {
        basePath = "/reports";
      } else if (currentPath.includes("/report-templates/")) {
        basePath = "/report-templates";
      }
      // The /chat viewer route keys on the bare UUID slug, while a chat
      // conversation's entityId is the pgid `_id`
      // (`pgid-chat-conversations-<uuid>`). Strip the prefix at this routing
      // boundary so the shared link matches the address bar; other base paths
      // key on the raw id and pass through unchanged.
      const pathId =
        basePath === "/chat" ? toConversationSlug(entityId) : entityId;
      url.pathname = `${basePath}/${pathId}`;
      // Only the report editors read `mode` — it drops them into read-only
      // viewing. The other viewers (chat, agents, matrix) ignore it, so don't
      // append noise to their links.
      if (basePath === "/reports" || basePath === "/report-templates") {
        url.searchParams.set("mode", "viewing");
      }
    }
    navigator.clipboard.writeText(url.toString());
    setLinkCopiedCount((n) => n + 1);
  }, [onCopyLink, entityId, entityType, entityName]);

  // ---- People with access list ----

  const currentUserPermission =
    sharingStatus?.current_user_permission ?? "edit";
  const ownerInfo = sharingStatus?.owner ?? null;
  // When the backend resolves an owner, trust it. When it can't, don't
  // optimistically assume the current user is the owner. Imported interactions
  // are authored by a system/cross-org account (e.g. "Finpilot Administrator")
  // that resolves to no real org user, so the backend returns owner=null — in
  // that case nobody is listed as owner and the current user is shown as a
  // plain access-holder. Views/workspaces never return an owner, so they keep
  // using the isOwner prop fallback. #9953
  const resolvedIsOwner = ownerInfo
    ? ownerInfo.id === userId
    : entityType === "interaction"
      ? false
      : isOwner;
  const isViewer = currentUserPermission === "view" && !resolvedIsOwner;
  // The organization reaches an artifact through its own grant, its agent's
  // (#11941) and its folder's (#14320). It is named once, at the highest of
  // them: the own grant in the general-access control, and a reach through
  // a source on its own line, with that source, only when it gives more than
  // the control shows (a viewer, who gets no control, always sees it).
  const inheritedOrg = useMemo((): {
    level: "view" | "edit";
    sourceType: string;
    sourceName: string;
  } | null => {
    const reaches = [
      sharingStatus?.inherited ?? null,
      sharingStatus?.inherited_folder ?? null,
    ].flatMap((source) =>
      source?.org_permission
        ? [
            {
              level:
                source.org_permission === "edit"
                  ? ("edit" as const)
                  : ("view" as const),
              sourceType: source.source_type,
              sourceName: source.source_name,
            },
          ]
        : []
    );
    const best =
      reaches.find((reach) => reach.level === "edit") ?? reaches[0] ?? null;
    if (!best) return null;
    const own = sharingStatus?.org_id
      ? sharingStatus.org_permission === "view"
        ? "view"
        : "edit"
      : null;
    const ownCovers =
      own === "edit" || (own === "view" && best.level === "view");
    return ownCovers && !isViewer ? null : best;
  }, [sharingStatus, isViewer]);
  // Whether the current user may CHANGE sharing (vs. merely having edit access
  // to the content). The backend sends this for interactions (author /
  // superadmin); for other entity types it's absent and we fall back to
  // ownership. The modal's editable controls gate on this so non-managers see
  // read-only state instead of edit affordances the backend would reject. #9954
  const canManageSharing = sharingStatus?.can_manage_sharing ?? resolvedIsOwner;
  const currentUserEmail: string | undefined =
    typeof userEmail === "string" && userEmail
      ? userEmail
      : typeof userName === "object" && userName?.email
        ? userName.email
        : undefined;

  const peopleWithAccess: AccessItem[] = useMemo(() => {
    const items: AccessItem[] = [];
    const ownerId = ownerInfo?.id ?? (resolvedIsOwner ? userId : null);

    if (ownerInfo && ownerInfo.id !== userId) {
      items.push({
        id: ownerInfo.id,
        name: ownerInfo.name,
        email: ownerInfo.email ?? undefined,
        kind: "user",
        role: "Owner",
        permissionType: "edit",
      });
    }

    items.push({
      id: userId || "self",
      name: displayName,
      email: currentUserEmail,
      kind: "user",
      role: resolvedIsOwner
        ? "Owner"
        : currentUserPermission === "view"
          ? "Viewer"
          : "Editor",
      permissionType: currentUserPermission === "view" ? "view" : "edit",
    });

    for (const u of sharingStatus?.users ?? []) {
      if (u.id === userId) continue;
      if (ownerId && u.id === ownerId) continue;
      items.push({
        id: u.id,
        name: u.name,
        email: u.email,
        kind: "user",
        role: u.type === "view" ? "Viewer" : "Editor",
        permissionType: u.type,
      });
    }

    for (const t of sharingStatus?.teams ?? []) {
      items.push({
        id: t.id,
        name: t.name,
        kind: "team",
        role: t.type === "view" ? "Viewer" : "Editor",
        permissionType: t.type,
      });
    }

    const listed = new Set(items.map((i) => `${i.kind}-${i.id}`));
    for (const u of inherited?.users ?? []) {
      if (listed.has(`user-${u.id}`)) continue;
      items.push({
        id: u.id,
        name: u.name,
        email: u.email,
        kind: "user",
        // At what the source grants (#13422): an Editor of the agent edits
        // what it produced.
        role: u.type === "view" ? "Viewer" : "Editor",
        permissionType: u.type,
        inheritedFrom: inherited?.source_name,
      });
    }
    for (const t of inherited?.teams ?? []) {
      if (listed.has(`team-${t.id}`)) continue;
      items.push({
        id: t.id,
        name: t.name,
        kind: "team",
        role: t.type === "view" ? "Viewer" : "Editor",
        permissionType: t.type,
        inheritedFrom: inherited?.source_name,
      });
    }

    // Access through a container (a view group, #13896; an artifact's
    // folder, #14320). Each grantee is listed once, at the highest level they
    // reach, labelled with where it comes from: a container that gives an
    // Editor what the item gives as Viewer takes the row, read-only. The
    // viewer's own row and an owner's stay as they are.
    const role = (type: string): "Editor" | "Viewer" =>
      type === "view" ? "Viewer" : "Editor";
    const add = (item: AccessItem) => {
      const key = `${item.kind}-${item.id}`;
      if (!listed.has(key)) {
        listed.add(key);
        items.push(item);
        return;
      }
      const at = items.findIndex((i) => `${i.kind}-${i.id}` === key);
      const listedItem = items[at];
      const isSelf = item.kind === "user" && item.id === (userId || "self");
      if (
        at !== -1 &&
        !isSelf &&
        listedItem.role !== "Owner" &&
        listedItem.permissionType === "view" &&
        item.permissionType === "edit"
      ) {
        items[at] = item;
      }
    };

    // A view's access through the view groups that hold it (#13896): each
    // group's owner (who edits every view in it), its people and teams, and
    // the organization, at what the group grants.
    for (const group of sharingStatus?.inherited_groups ?? []) {
      const from = {
        inheritedFrom: group.group_name,
        inheritedKind: "group" as const,
      };
      if (group.owner) {
        add({
          id: group.owner.id,
          name: group.owner.name,
          kind: "user",
          role: "Editor",
          permissionType: "edit",
          ...from,
        });
      }
      for (const u of group.users) {
        add({
          id: u.id,
          name: u.name,
          email: u.email,
          kind: "user",
          role: role(u.type),
          permissionType: u.type,
          ...from,
        });
      }
      for (const t of group.teams) {
        add({
          id: t.id,
          name: t.name,
          kind: "team",
          role: role(t.type),
          permissionType: t.type,
          ...from,
        });
      }
      if (group.org_permission) {
        add({
          id: group.org_id ?? "organization",
          name: `Everyone at ${organizationName}`,
          kind: "organization",
          role: role(group.org_permission),
          permissionType: group.org_permission === "view" ? "view" : "edit",
          ...from,
        });
      }
    }

    // An artifact's audience through the folder it is filed in (#14320): the
    // folder's people, teams and organization at what the folder grants.
    const folderAccess = sharingStatus?.inherited_folder ?? null;
    if (folderAccess) {
      const fromFolder = {
        inheritedFrom: folderAccess.source_name,
        inheritedKind: "folder" as const,
      };
      for (const u of folderAccess.users) {
        add({
          id: u.id,
          name: u.name,
          email: u.email,
          kind: "user",
          role: role(u.type),
          permissionType: u.type,
          ...fromFolder,
        });
      }
      for (const t of folderAccess.teams) {
        add({
          id: t.id,
          name: t.name,
          kind: "team",
          role: role(t.type),
          permissionType: t.type,
          ...fromFolder,
        });
      }
      // The folder's organization grant is not a row: the organization is
      // named once, in general access (``inheritedOrg``).
    }

    if (pendingUser) {
      items.push({
        id: pendingUser.id,
        name: pendingUser.name,
        email: pendingUser.email,
        kind: "user",
        role: pendingType === "view" ? "Viewer" : "Editor",
        permissionType: pendingType,
        unsaved: true,
      });
    }

    return items;
  }, [
    pendingUser,
    pendingType,
    sharingStatus,
    organizationName,
    inherited,
    userId,
    displayName,
    resolvedIsOwner,
    currentUserPermission,
    ownerInfo,
    currentUserEmail,
  ]);

  // The groups a view's admin may take it out of (#13896).
  const removableGroups =
    entityType === "view" &&
    onRemoveFromGroup &&
    sharingStatus?.can_remove_from_groups === true
      ? (sharingStatus?.inherited_groups ?? [])
      : [];
  const queryClient = useQueryClient();
  // The group being taken out, while its removal is saved.
  const [removingGroupId, setRemovingGroupId] = useState<number | null>(null);
  const removeFromGroup = (groupId: number) => {
    if (!onRemoveFromGroup) return;
    setRemovingGroupId(groupId);
    void onRemoveFromGroup(groupId)
      .catch(() => undefined)
      .finally(() => {
        setRemovingGroupId(null);
        void queryClient.invalidateQueries({
          queryKey: SHARING_QUERY_KEYS.status(entityType, entityId),
        });
      });
  };

  // ---- Email recipients (#10815, every entity type since Hunter's ruling) ----
  // Teams can't be mailed (a team is not a mailbox) and you don't email
  // yourself, so the candidate list is the individual access-holders minus the
  // current user. An access-holder with no resolved email is dropped rather
  // than offered and rejected by the backend. Organization-wide and link
  // access are not expanded into every member: the compose step says so
  // instead of claiming nobody has access. A holder the backend marks not
  // emailable (Finpilot staff without access open right now) is dropped the
  // same way.
  const sharedWithEveryone =
    generalAccess !== "restricted" ||
    inheritedOrgAccess ||
    !!sharingStatus?.inherited_folder?.org_permission;
  const emailRecipients = useMemo(() => {
    const notEmailable = new Set(
      [...(sharingStatus?.users ?? []), ...(ownerInfo ? [ownerInfo] : [])]
        .filter((holder) => holder.emailable === false)
        .map((holder) => holder.id)
    );
    return peopleWithAccess
      .filter(
        (p) =>
          p.kind === "user" &&
          p.id !== userId &&
          !!p.email &&
          !notEmailable.has(p.id)
      )
      .map((p) => ({ id: p.id, name: p.name, email: p.email }));
  }, [peopleWithAccess, userId, sharingStatus, ownerInfo]);

  const handleSendEmail = useCallback(
    (recipientIds: string[], message: string) => {
      setEmailError(null);
      notifyMutation.mutate(
        {
          entityType,
          entityId,
          payload: {
            recipient_user_ids: recipientIds,
            ...(message.trim() ? { message: message.trim() } : {}),
          },
        },
        {
          onSuccess: (result) => {
            setEmailSkipped(result.skipped ?? []);
            setEmailSentCount(result.sent_count);
          },
          onError: (err: unknown) =>
            setEmailError(
              err instanceof Error ? err.message : "Failed to send the email"
            ),
        }
      );
    },
    [entityType, entityId, notifyMutation]
  );

  const handleLeaveEmailMode = useCallback(() => {
    setEmailMode(false);
    setEmailSentCount(null);
    setEmailSkipped([]);
    setEmailError(null);
  }, []);

  // Escape dismisses one surface at a time (#11426 nooks N1) — the email
  // compose panel stands down before the dialog does, and the ink menus
  // stop propagation before either. The legacy modal had no keyboard
  // path out at all.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (emailMode) {
        handleLeaveEmailMode();
        return;
      }
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, emailMode, handleLeaveEmailMode]);

  // ---- Render ----

  if (!open) return null;

  // Email compose step (#10815). Rendered as its own view rather than inline
  // in the sharing body so the sharing markup below stays untouched.
  if (emailMode) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div
          className="absolute inset-0"
          style={{ background: "var(--fp-scrim)" }}
          onClick={onClose}
        />
        <div
          role="dialog"
          aria-label={`Email “${entityName}”`}
          className="relative bg-[var(--fp-surface)] text-[var(--fp-ink)] rounded-lg [box-shadow:var(--fp-lift-overlay)] w-full max-w-md mx-4 max-h-[90vh] overflow-hidden"
          style={{
            fontFamily: "var(--fp-font-ui)",
            fontFeatureSettings: "var(--fp-feat-ui)",
          }}
        >
          <div className="flex items-center justify-between p-6 pb-4">
            <h2
              className="text-[var(--fp-ink)] truncate"
              style={{
                fontSize: "var(--fp-t-lede)",
                fontWeight: 600,
                letterSpacing: "var(--fp-track-head)",
              }}
            >
              Email &ldquo;{entityName}&rdquo;
            </h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-1 text-[var(--fp-ink-4)] hover:text-[var(--fp-ink)] transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <div className="px-6 pb-6">
            <EmailNotePanel
              noun={emailNoun}
              sharedWithEveryone={sharedWithEveryone}
              recipients={emailRecipients}
              onBack={handleLeaveEmailMode}
              onSend={handleSendEmail}
              isSending={notifyMutation.isPending}
              errorMessage={emailError}
              sentCount={emailSentCount}
              skipped={emailSkipped}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: "var(--fp-scrim)" }}
        onClick={onClose}
      />

      {/* The token dialog card (#11426 nooks N1): the M2 shell — head,
          body, a ruled footer — on the design system's ground and type.
          The explicit ink base still matters: the modal renders inside its
          caller's subtree, and the chat sidebar sets `text-white` on its
          container. */}
      <div
        role="dialog"
        aria-label={`Share “${entityName}”`}
        className="relative bg-[var(--fp-surface)] text-[var(--fp-ink)] rounded-lg [box-shadow:var(--fp-lift-overlay)] w-full max-w-md mx-4 max-h-[90vh] overflow-hidden"
        style={{
          fontFamily: "var(--fp-font-ui)",
          fontFeatureSettings: "var(--fp-feat-ui)",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4">
          <h2
            className="text-[var(--fp-ink)] truncate"
            style={{
              fontSize: "var(--fp-t-lede)",
              fontWeight: 600,
              letterSpacing: "var(--fp-track-head)",
            }}
          >
            Share &ldquo;{entityName}&rdquo;
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1 text-[var(--fp-ink-4)] hover:text-[var(--fp-ink)] transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {/* Skeleton loader */}
          {isLoading && (
            <div className="space-y-4 animate-pulse">
              {/* Search bar skeleton */}
              <div className="h-10 bg-[var(--fp-band)] rounded-lg" />

              {/* "People with access" heading skeleton */}
              <div>
                <div className="h-4 w-36 bg-[var(--fp-band)] rounded mb-3" />
                {/* Person rows */}
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2"
                  >
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-[var(--fp-band)] rounded-full mr-3" />
                      <div className="h-3.5 w-28 bg-[var(--fp-band)] rounded" />
                    </div>
                    <div className="h-6 w-16 bg-[var(--fp-band)] rounded" />
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div className="border-t border-[var(--fp-border)]" />

              {/* General access skeleton */}
              <div>
                <div className="h-4 w-28 bg-[var(--fp-band)] rounded mb-3" />
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-[var(--fp-band)] rounded" />
                  <div className="h-10 flex-1 bg-[var(--fp-band)] rounded-lg" />
                </div>
              </div>

              {/* Footer skeleton */}
              <div className="flex justify-between pt-2">
                <div className="h-9 w-24 bg-[var(--fp-band)] rounded-lg" />
                <div className="h-9 w-16 bg-[var(--fp-band)] rounded-lg" />
              </div>
            </div>
          )}

          {/* Adding people changes the sharing: someone the server says
              can edit the content but not manage its sharing (an agent's
              Editor on an artifact, #13422) gets no picker whose every add
              is refused. Types that send no flag keep today's picker. */}
          {!isLoading &&
            !isViewer &&
            sharingStatus?.can_manage_sharing !== false && (
              <GranteePicker
                users={searchResults.users}
                teams={searchResults.teams}
                otherMemberCount={searchResults.otherMemberCount}
                statusFailed={sharingStatusFailed}
                query={searchQuery}
                onQueryChange={setSearchQuery}
                showResults={showSearchResults}
                onShowResultsChange={setShowSearchResults}
                onAddUser={handleAddUser}
                onAddTeam={handleAddTeam}
              />
            )}

          {!isLoading && !isViewer && !!componentCount && (
            <p
              data-testid="dashboard-widgets-shared"
              className="text-sm text-[var(--fp-ink-2)]"
            >
              {`Sharing this dashboard also shares the ${componentCount} ${plural(componentCount, "component", "components")} its widgets use.`}
            </p>
          )}

          {!isLoading && (
            <AccessList
              heading="People with access"
              items={peopleWithAccess}
              currentUserId={userId}
              canManage={!isViewer && canManageSharing}
              onChangePermission={handleChangePermission}
              onRemove={handleRemoveAccess}
            />
          )}

          {!isLoading && removableGroups.length > 0 && (
            // The view's admin may take it out of a group that holds it
            // (#13896): the group's audience then loses it, unless it reaches
            // them some other way. One action per group, not per grantee.
            <div className="mt-3 space-y-1">
              {removableGroups.map((group) => (
                <div
                  key={group.group_id}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="min-w-0 truncate text-xs text-[var(--fp-ink-3)]">
                    {group.group_name}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove from ${group.group_name}`}
                    disabled={removingGroupId !== null}
                    onClick={() => removeFromGroup(group.group_id)}
                    className="flex-shrink-0 rounded px-2 py-1 text-xs text-[var(--fp-ink-2)] hover:bg-[var(--fp-hover)] hover:text-[var(--fp-ink)] disabled:opacity-50"
                  >
                    Remove from group
                  </button>
                </div>
              ))}
            </div>
          )}

          {!isLoading && showOrgToggle && !isViewer && (
            <>
              <div className="border-t border-[var(--fp-border)] my-4" />
              <GeneralAccessControl
                value={generalAccess}
                orgPermission={
                  sharingStatus?.org_permission === "view" ? "view" : "edit"
                }
                organizationName={organizationName}
                showLinkSharing={showLinkSharing}
                canManage={
                  canManageSharing &&
                  sharingStatus?.can_manage_organization_sharing !== false
                }
                allowOrgEdit={entityType !== "component"}
                caption={
                  generalAccess === "restricted"
                    ? "Only people with access can view"
                    : generalAccess === "organization"
                      ? `Anyone in ${organizationName} can ${sharingStatus?.org_permission === "view" ? "view" : "edit"}`
                      : "Anyone with this link in the organization can view"
                }
                onChange={handleGeneralAccessChange}
                onOrgPermissionChange={handleOrgPermissionChange}
              />
            </>
          )}

          {!isLoading && inheritedOrg && (
            <div className={isViewer ? "" : "mt-4"}>
              {/* The organization reaches this through the source, beside
                  whatever the entity's own general access says: the control
                  above governs the entity's own grant, and this line cannot
                  be lowered here — it is the source's sharing to change
                  (#11941). Viewers, who get no control, still see it. */}
              {isViewer && (
                <>
                  <div className="border-t border-[var(--fp-border)] my-4" />
                  <h3
                    className="uppercase tracking-wider text-[var(--fp-ink-3)] mb-3"
                    style={{ fontSize: "var(--fp-t-fine)", fontWeight: 600 }}
                  >
                    General access
                  </h3>
                </>
              )}
              <div className="flex items-center gap-2">
                <Buildings
                  size={20}
                  weight="regular"
                  className="text-[var(--fp-ink-2)] flex-shrink-0"
                />
                <span className="flex-1 text-sm font-medium text-[var(--fp-ink)] truncate">
                  {organizationName}
                </span>
                <span className="text-sm font-medium text-[var(--fp-ink)]">
                  {inheritedOrg.level === "edit" ? "Editor" : "Viewer"}
                </span>
              </div>
              <p className="text-xs text-[var(--fp-ink-3)] mt-2 ml-8">
                {`Anyone in ${organizationName} can ${inheritedOrg.level === "edit" ? "edit" : "view"}, through the ${inheritedOrg.sourceType} ${inheritedOrg.sourceName}`}
              </p>
            </div>
          )}

          {shareError && (
            <p role="alert" className="text-sm text-red-600">
              {shareError}
            </p>
          )}

          {pendingMailFailed && (
            <p role="alert" className="text-sm text-red-600">
              Shared, but the email to them could not be sent.
            </p>
          )}

          {notShareableWidgets.length > 0 && (
            <p
              data-testid="dashboard-widgets-unshared"
              className="text-sm text-[var(--fp-ink-2)]"
            >
              {`${notShareableWidgets.length} ${plural(notShareableWidgets.length, "widget", "widgets")} on this dashboard that you cannot see could not be shared.`}
            </p>
          )}

          {notOrganizationWideWidgets.map((widget) => (
            <p
              key={widget.component_id}
              data-testid="dashboard-widget-not-organization-wide"
              className="text-sm text-[var(--fp-ink-2)]"
            >
              {`${widget.name} is shared with the people above but not with everyone in the organization; its owner or an organization admin can share it organization-wide.`}
            </p>
          ))}

          {/* Footer: the M2 shell's ruled footer (#11426 nooks N1) — no
              destructive action here, so the left slot carries Copy link
              as a secondary text button and Done sits right as the only
              filled control. */}
          {!isLoading && (
            <div className="flex justify-between items-center pt-4 border-t border-[var(--fp-border-strong)]">
              <div className="flex items-center gap-3">
                {showCopyLink ? (
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center text-[var(--fp-ink-2)] hover:text-[var(--fp-ink)] transition-colors"
                  >
                    {linkCopied ? (
                      <Check
                        size={16}
                        weight="bold"
                        className="mr-2 text-green-600"
                      />
                    ) : (
                      <LinkIcon size={16} weight="regular" className="mr-2" />
                    )}
                    <span className="text-sm">
                      {linkCopied ? "Copied!" : "Copy link"}
                    </span>
                  </button>
                ) : (
                  <div />
                )}
                {/* describeChild keeps the visible "Email" as the button's
                    accessible name; the hint stays a description. */}
                <Tooltip
                  title={`Email this ${emailNoun} to the people with access`}
                  describeChild
                >
                  <button
                    onClick={() => setEmailMode(true)}
                    className="flex items-center text-[var(--fp-ink-2)] hover:text-[var(--fp-ink)] transition-colors"
                  >
                    <EnvelopeSimple
                      size={16}
                      weight="regular"
                      className="mr-2"
                    />
                    <span className="text-sm">Email</span>
                  </button>
                </Tooltip>
              </div>
              {pendingUser ? (
                <button
                  onClick={handleSavePending}
                  disabled={shareMutation.isPending}
                  className="px-3.5 py-1.5 bg-[var(--fp-ink)] text-[var(--fp-surface)] rounded-md hover:bg-[var(--fp-ink-2)] transition-colors disabled:opacity-60"
                >
                  <span className="text-sm">Save</span>
                </button>
              ) : (
                <button
                  onClick={onClose}
                  className="px-3.5 py-1.5 bg-[var(--fp-ink)] text-[var(--fp-surface)] rounded-md hover:bg-[var(--fp-ink-2)] transition-colors"
                >
                  <span className="text-sm">Done</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
